import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React from 'react';
import { useTranslation } from 'react-i18next';
import Flex, { Box } from 'components/Flex';
import Button from 'components/Button';
import Icon from 'components/Icon';
import FormField, { DefaultFormFieldLayout } from 'components/FormField';
import FormStack from 'components/FormStack';
import ShadedCard from 'components/ShadedCard';
import Panel from 'components/Panel';
import Switch from 'components/Switch';
import { Option, Select } from 'components/Select';
import CheckboxOption from 'components/CheckboxOption';
import Alert, { AlertTypes } from 'components/Alert';
import Heading from 'components/Heading';
import useUniqueId from 'hooks/useUniqueId';
import useModalQueryParam from 'hooks/useModalQueryParam';
import DisconnectConfirmationModal from './DisconnectConfirmationModal';
import { useAutoJoinForm } from './hooks/useAutoJoinForm';
import { useDisconnectMyCalendar } from './hooks/useDisconnectMyCalendar';
import { useUpdateMyCalendarAutoJoinSettings } from './hooks/useUpdateMyCalendarAutoJoinSettings';
function CalendarLinkedContent(_ref) {
  var settings = _ref.settings;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useModalQueryParam = useModalQueryParam('disconnect-calendar'),
    _useModalQueryParam2 = _slicedToArray(_useModalQueryParam, 2),
    hasDisconnectConfirmationModal = _useModalQueryParam2[0],
    toggleDisconnectConfirmationModal = _useModalQueryParam2[1];
  var autoJoinForm = useAutoJoinForm({
    isAutoJoinEnabled: settings.isAutoJoinEnabled,
    autoJoinSettings: settings.autoJoinSettings
  });
  var fields = autoJoinForm.fields,
    hasErrors = autoJoinForm.hasErrors,
    wasChanged = autoJoinForm.wasChanged,
    reset = autoJoinForm.reset;
  var _useDisconnectMyCalen = useDisconnectMyCalendar({
      showAlert: false
    }),
    disconnectMyCalendar = _useDisconnectMyCalen.disconnectMyCalendar,
    isDisconnectLoading = _useDisconnectMyCalen.isLoading;
  var _useUpdateMyCalendarA = useUpdateMyCalendarAutoJoinSettings({
      onSuccess: reset
    }),
    updateMyCalendarAutoJoinSettings = _useUpdateMyCalendarA.updateMyCalendarAutoJoinSettings,
    isUpdateLoading = _useUpdateMyCalendarA.isLoading;
  function onSubmit(_x) {
    return _onSubmit.apply(this, arguments);
  }
  function _onSubmit() {
    _onSubmit = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(e) {
      var updatedSettings;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            e.preventDefault();
            updatedSettings = {
              isAutoJoinEnabled: fields.isAutoJoinEnabled.value,
              autoJoinSettings: {
                meeting_type: fields.meetingType.value,
                only_meetings_I_organise: fields.onlyMeetingsIOrganise.value
              }
            };
            _context.next = 4;
            return updateMyCalendarAutoJoinSettings(updatedSettings);
          case 4:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return _onSubmit.apply(this, arguments);
  }
  var enabledFieldId = useUniqueId();
  var meetingTypeFieldId = useUniqueId();
  var hasActiveSubscription = settings.hasActiveSubscription;
  var isGoogleCalendar = settings.provider === 'google';
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    mb: "l"
  }, hasActiveSubscription ? /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "space-between",
    gap: "s",
    mb: "xxs"
  }, /*#__PURE__*/React.createElement(Flex, {
    gap: "xs"
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "calendar",
    color: "alert-success"
  }), t(isGoogleCalendar ? 'state_google_calendar_connected' : 'state_microsoft_calendar_connected')), /*#__PURE__*/React.createElement(Button, {
    size: "medium",
    onClick: toggleDisconnectConfirmationModal
  }, t('actions_disconnect'))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "space-between",
    gap: "s",
    mb: "xxs"
  }, /*#__PURE__*/React.createElement(Flex, {
    gap: "xs"
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "calendar",
    color: "alert-fail"
  }), t(isGoogleCalendar ? 'state_google_calendar_disconnected' : 'state_microsoft_calendar_disconnected')), /*#__PURE__*/React.createElement(Button, {
    size: "medium",
    variant: "important",
    onClick: function onClick() {
      return disconnectMyCalendar(settings.provider);
    },
    isLoading: isDisconnectLoading,
    isDisabled: isUpdateLoading
  }, t('actions_reconnect'))), /*#__PURE__*/React.createElement(Box, {
    my: "s"
  }, /*#__PURE__*/React.createElement(Alert, {
    type: AlertTypes.FAIL
  }, t('alert_calendar_disconnected'))))), /*#__PURE__*/React.createElement("form", {
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement(ShadedCard, {
    p: "s",
    mb: "m"
  }, /*#__PURE__*/React.createElement(Heading, {
    as: "h3",
    mb: "m",
    size: "small"
  }, t('auto_join_settings_form_title')), /*#__PURE__*/React.createElement(DefaultFormFieldLayout, {
    horizontal: true
  }, /*#__PURE__*/React.createElement(FormStack, null, /*#__PURE__*/React.createElement(FormField, {
    required: true,
    label: t('state_enabled'),
    id: enabledFieldId
  }, /*#__PURE__*/React.createElement(Switch, {
    id: enabledFieldId,
    onChange: function onChange() {
      return fields.isAutoJoinEnabled.set(!fields.isAutoJoinEnabled.value);
    },
    checked: fields.isAutoJoinEnabled.value,
    disabled: !hasActiveSubscription
  })), /*#__PURE__*/React.createElement(FormField, {
    required: true,
    as: "span",
    label: t('auto_join_settings_form_meetings_title'),
    labelId: meetingTypeFieldId
  }, /*#__PURE__*/React.createElement(FormStack, null, /*#__PURE__*/React.createElement(Select, {
    labelledById: meetingTypeFieldId,
    size: "medium",
    menuPlacement: "bottom",
    value: fields.meetingType.value || 'all',
    onChange: function onChange(value) {
      return fields.meetingType.set(value);
    },
    isDisabled: !hasActiveSubscription
  }, /*#__PURE__*/React.createElement(Option, {
    value: "all"
  }, t('auto_join_settings_form_meetings_all')), /*#__PURE__*/React.createElement(Option, {
    value: "external"
  }, t('auto_join_settings_form_meetings_only_external')), /*#__PURE__*/React.createElement(Option, {
    value: "internal"
  }, t('auto_join_settings_form_meetings_only_internal'))), /*#__PURE__*/React.createElement(CheckboxOption, {
    label: t('auto_join_settings_form_meetings_only_organised_by_me'),
    disabled: !hasActiveSubscription,
    checked: !!fields.onlyMeetingsIOrganise.value,
    onChange: function onChange() {
      return fields.onlyMeetingsIOrganise.set(!fields.onlyMeetingsIOrganise.value);
    }
  })))))), /*#__PURE__*/React.createElement(Panel, {
    align: "center",
    flush: true
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    icon: "ok",
    variant: "primary",
    isDisabled: hasErrors || !wasChanged || !hasActiveSubscription || isDisconnectLoading,
    isLoading: isUpdateLoading
  }, t('actions_save')))), hasDisconnectConfirmationModal && /*#__PURE__*/React.createElement(DisconnectConfirmationModal, {
    onClose: toggleDisconnectConfirmationModal,
    provider: settings.provider
  }));
}
export default CalendarLinkedContent;